import { useCallback, useState, type ReactNode } from 'react';
import { Input } from '@amzn/stencil-react-components/input';
import { Row, Col } from '@amzn/stencil-react-components/layout';
import {
  FilterBar,
  FilterBarSize,
  FilterController,
  FilterList,
} from '@amzn/stencil-react-components/filtering';
import { Menu, MenuList, MenuItem } from '@amzn/stencil-react-components/menu';
import { AuditStatus, AuditType } from '@amzn/gbsa-helios-typescript-client';

import { useMessages } from '../../../shared/i18n/useMessages';
import { trackInteraction, INTERACTION_TYPES } from '../../../shared/observability';
import { buildDateRangeSelection } from '../../../shared/domain';
import { useDateRangeFilterElement } from '../../../shared/ui/useDateRangeFilterElement';
import { useLocationFilterElement } from '../../../shared/ui/useLocationFilterElement';
import { OverflowMenuTrigger } from '../../../shared/ui/OverflowMenuTrigger';
import { AUDIT_PAGES } from '../tracking';
import { useAuditFilterHandlers } from '../hooks/useAuditFilterHandlers';
import type { AuditListFilters } from '../hooks/useAuditListFilters';

const LIST_BUNDLE = 'features.audit.translations.auditList';
type ListKey =
  | 'searchPlaceholder'
  | 'filterFlyoutTitle'
  | 'filterStatusTitle'
  | 'filterStatusClear'
  | 'filterStatusListTitle'
  | 'filterAuditTypeTitle'
  | 'filterAuditTypeClear'
  | 'filterAuditTypeListTitle'
  | 'filterLocationTitle'
  | 'filterLocationClear'
  | 'filterLocationListTitle'
  | 'filterTemplateTitle'
  | 'filterTemplateClear'
  | 'filterTemplateListTitle'
  | 'filterDivisionTitle'
  | 'filterDivisionClear'
  | 'filterDivisionListTitle'
  | 'filterDateRangeTitle'
  | 'filterDateRangeClear'
  | 'filterDateRangeListTitle'
  | 'statusNotStarted'
  | 'statusInProgress'
  | 'statusCompleted'
  | 'statusExpired'
  | 'statusCancelled'
  | 'statusScheduled'
  | 'auditTypeProgress'
  | 'auditTypeMPA'
  | 'auditTypeCalibration'
  | 'auditTypeTest'
  | 'auditTypeSituational'
  | 'createAuditMenuItem'
  | 'ariaMoreActions';

interface LocationFilter {
  value: string | undefined;
  options: readonly string[];
  setValue: (value: string | undefined) => void;
}

interface AuditListFilterBarProps {
  filters: AuditListFilters;
  locationFilter: LocationFilter;
  templateOptions: { label: string; value: string }[];
  divisionOptions: { label: string; value: string }[];
  /** Whether to show the 3-dot overflow menu (hidden when user has no create permissions). */
  showOverflowMenu?: boolean;
  /** Called when "Create Audit" is tapped in the overflow menu. */
  onCreateAudit?: () => void;
  /**
   * Optional node rendered inline to the RIGHT of the search input,
   * BEFORE the overflow menu (so visual order is
   * `search input → refresh → 3-dot`). Typically a `RefreshButton`.
   * Mirrors the slot pattern used by `TaskListFilterBar.rightSlot` —
   * the bar owns the search row's layout, callers plug in adjacent
   * controls without re-hosting the search input themselves.
   */
  rightSlot?: ReactNode;
  setFilter: <K extends keyof Omit<AuditListFilters, 'keyword'>>(
    key: K,
    value: AuditListFilters[K],
  ) => void;
  applyFilters: (partial: Partial<Omit<AuditListFilters, 'keyword'>>) => void;
  setKeyword: (value: string) => void;
}

/**
 * Search bar and FilterBar for the audit list.
 * The search input matches both audit keywords and assignees server-side,
 * so a single search field covers both — no toggle needed.
 *
 * standards-ignore: DRY:FilterController standards-ignore: DRY:FilterList
 */
export function AuditListFilterBar({
  filters,
  locationFilter,
  templateOptions,
  divisionOptions,
  showOverflowMenu = false,
  onCreateAudit,
  rightSlot,
  setFilter,
  applyFilters,
  setKeyword,
}: AuditListFilterBarProps): JSX.Element {
  const { t } = useMessages<ListKey>(LIST_BUNDLE);

  const auditStatusOptions = [
    { label: t('statusNotStarted'), value: AuditStatus.NOT_STARTED },
    { label: t('statusInProgress'), value: AuditStatus.IN_PROGRESS },
    { label: t('statusCompleted'), value: AuditStatus.COMPLETED },
    { label: t('statusExpired'), value: AuditStatus.EXPIRED },
    { label: t('statusCancelled'), value: AuditStatus.CANCELLED },
    { label: t('statusScheduled'), value: AuditStatus.SCHEDULED },
  ];

  const auditTypeOptions = [
    { label: t('auditTypeProgress'), value: AuditType.PROGRESS },
    { label: t('auditTypeMPA'), value: AuditType.MPA },
    { label: t('auditTypeCalibration'), value: AuditType.CALIBRATION },
    { label: t('auditTypeTest'), value: AuditType.TEST },
    { label: t('auditTypeSituational'), value: AuditType.SITUATION },
  ];

  const handleSearchFocus = useCallback(() => {
    trackInteraction({ type: INTERACTION_TYPES.searchFocused, page: AUDIT_PAGES.list });
  }, []);

  const {
    handleKeywordChange,
    handleStatusChange,
    handleAuditTypeChange,
    handleLocationChange,
    handleTemplateChange,
    handleDivisionChange,
    handleDateRangeTypeChange,
  } = useAuditFilterHandlers({
    setFilter,
    setKeyword,
  });

  // Local state for the custom date range picker — the FilterBar.onApply
  // callback reads these at commit time when the user selects a CUSTOM
  // range in the flyout.
  const [customStartDate, setCustomStartDate] = useState<string | undefined>(
    filters.dateRange?.startDate,
  );
  const [customEndDate, setCustomEndDate] = useState<string | undefined>(
    filters.dateRange?.endDate,
  );

  // Shared filter-controller elements must be produced by hooks (not wrapper
  // components) so FilterBar's `React.Children.forEach + child.type ===
  // FilterController` check accepts them — a wrapper would be silently
  // skipped and the chip would never render.
  const locationFilterElement = useLocationFilterElement({
    options: locationFilter.options,
    value: filters.locations,
    onChange: handleLocationChange,
    titleText: t('filterLocationTitle'),
    resetButtonText: t('filterLocationClear'),
    listTitleText: t('filterLocationListTitle'),
  });

  const dateRangeFilterElement = useDateRangeFilterElement({
    filterDateRange: filters.dateRange,
    customStartDate,
    customEndDate,
    onTypeChange: handleDateRangeTypeChange,
    onCustomDatesChange: (start, end) => {
      setCustomStartDate(start);
      setCustomEndDate(end);
    },
    onApplyCustomRange: (range) => applyFilters({ dateRange: range }),
    titleText: t('filterDateRangeTitle'),
    resetButtonText: t('filterDateRangeClear'),
    listTitleText: t('filterDateRangeListTitle'),
  });

  return (
    <>
      <Row gridGap="dimensions.spacing.200" alignItems="center">
        <Col flex="1" minWidth={0}>
          <Input
            placeholder={t('searchPlaceholder')}
            value={filters.keyword}
            onChange={handleKeywordChange}
            onFocus={handleSearchFocus}
          />
        </Col>
        {/* Refresh (or any adjacent control) sits between the search
            input and the overflow menu so the destructive/creative
            3-dot menu stays anchored to the far right. */}
        {rightSlot}
        {showOverflowMenu && (
          <Menu
            triggerComponent={<OverflowMenuTrigger ariaLabel={t('ariaMoreActions')} />}
          >
            <MenuList>
              <MenuItem onClick={() => onCreateAudit?.()}>
                {t('createAuditMenuItem')}
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </Row>

      {/* Filters — Status and Audit Type inline, rest in "All filters" flyout.
          standards-ignore: DRY:FilterController standards-ignore: DRY:FilterList */}
      <FilterBar
        flyoutTitleText={t('filterFlyoutTitle')}
        size={FilterBarSize.Small}
        visibleFiltersMaxCount={2}
        onApply={(selections) => {
          const toArray = (val: unknown): string[] =>
            Array.isArray(val)
              ? val.map(String)
              : typeof val === 'string' || typeof val === 'number'
                ? [String(val)]
                : [];
          const dateRangeType = selections.dateRange ? String(selections.dateRange) : '';
          // Applying a non-custom range (preset, chip dismissal, clear all)
          // must also drop the stored custom dates, or the next Apply/reopen
          // resurrects them. onFilterChange can't cover this: flyout radios
          // never fire it.
          if (dateRangeType !== 'CUSTOM') {
            setCustomStartDate(undefined);
            setCustomEndDate(undefined);
          }
          const dateRangeFilter = buildDateRangeSelection(
            dateRangeType,
            customStartDate,
            customEndDate,
          );
          applyFilters({
            statuses: toArray(selections.statuses),
            auditTypes: toArray(selections.auditTypes),
            templateIds: toArray(selections.templateIds),
            divisionName: selections.divisionName ? String(selections.divisionName) : '',
            locations: toArray(selections.locations),
            dateRange: dateRangeFilter,
          });
        }}
      >
        <FilterController name="statuses" titleText={t('filterStatusTitle')}>
          <FilterList
            isMulti
            onFilterChange={handleStatusChange}
            options={auditStatusOptions}
            resetButtonText={t('filterStatusClear')}
            titleText={t('filterStatusListTitle')}
            value={filters.statuses}
          />
        </FilterController>
        <FilterController name="auditTypes" titleText={t('filterAuditTypeTitle')}>
          <FilterList
            isMulti
            onFilterChange={handleAuditTypeChange}
            options={auditTypeOptions}
            resetButtonText={t('filterAuditTypeClear')}
            titleText={t('filterAuditTypeListTitle')}
            value={filters.auditTypes}
          />
        </FilterController>
        {locationFilterElement}
        <FilterController name="templateIds" titleText={t('filterTemplateTitle')}>
          <FilterList
            isMulti
            onFilterChange={handleTemplateChange}
            options={templateOptions}
            resetButtonText={t('filterTemplateClear')}
            titleText={t('filterTemplateListTitle')}
            value={filters.templateIds}
            isSearchable
          />
        </FilterController>
        <FilterController name="divisionName" titleText={t('filterDivisionTitle')}>
          {/* isSearchable renders a filter input inside the flyout row so
              long division lists don't require a drill-in navigation to
              reach a specific option — matches the Template chip's UX
              and V1 desktop's Multiselect filtering behavior. Division
              stays single-select because both the SDK
              (SearchAuditsInput.divisionName is a string) and V1
              desktop use single-select here. */}
          <FilterList
            onFilterChange={handleDivisionChange}
            options={divisionOptions}
            resetButtonText={t('filterDivisionClear')}
            titleText={t('filterDivisionListTitle')}
            value={filters.divisionName ? [filters.divisionName] : []}
            isSearchable
          />
        </FilterController>
        {dateRangeFilterElement}
      </FilterBar>
    </>
  );
}
